import { Typography } from '@mui/material';
import GlobalSearcher, { Filter } from 'lib/common/GlobalSearcher';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  getAllPayouts,
  getRecieverFactoringRequest,
} from '../../../../../../lib/api';
import { getCompanyPayoutGroup } from '../../../../../../lib/api/payments';
import CustomAlert from '../../../../../../lib/designSystem/Alert';
import CenteredCircularProgress from '../../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions, {
  CustomButtonProps,
} from '../../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../../lib/designSystem/Table';
import { hasAnyPermissions } from '../../../../../../lib/functions/utils';
import { AuthState } from '../../../../../../services/redux/authSlice';
import PayOutDetails from './components/Details';
import NewPayOut from './components/MainDialog';
import WithoutPayroll from './components/WithoutPayroll';

const headers: { key: string; label: string; type?: string }[] = [
  // { key: 'businessOriginName', label: 'Cuenta Origen' },
  // { key: 'bankAccountOriginName', label: 'Banco Origen' },
  { key: 'businessName', label: 'Cliente destino' },
  { key: 'businessIdentifier', label: 'Rut destino' },
  // { key: 'bankAccountDestinationName', label: 'Banco Destino' },
  { key: 'sourceTranslation', label: 'Fuente' },
  { key: 'sourceId', label: 'Id', type: 'numeric-id' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'executedAt', label: 'Completada', type: 'date' },
];

const payoutsRawHeaders: { key: string; label: string; type?: string }[] = [
  { key: 'bankAccountOriginName', label: 'Banco Origen' },
  // { key: 'bankAccountOriginName', label: 'Banco Origen' },
  { key: 'businessDestinationName', label: 'Cliente Destino' },
  { key: 'bankAccountDestinationName', label: 'Banco Destino' },
  { key: 'sourceTranslation', label: 'Fuente' },
  { key: 'sourceId', label: 'Id', type: 'numeric-id' },
  { key: 'amount', label: 'Monto', type: 'money' },
  { key: 'executedAt', label: 'Completada', type: 'date' },
];

interface Payout {
  source: string;
  sourceId: string;
  businessName: string;
  businessIdentifier: string;
  sourceTranslation: string;
  amount: number;
  executedAt: string;
  bankAccountOriginName: string;
  bankAccountDestinationName: string;
  bankAccountOriginId: string;
  bankAccountDestinationId: string;
}

const Payouts: React.FC = () => {
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const dispatch = useDispatch();
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [success, setSuccess] = useState('');
  const [failure, setFailure] = useState('');
  const [openDetails, setOpenDetails] = useState(false);
  const [filteredPayouts, setFilteredPayouts] = useState<Payout[]>([]);
  const [allPayOuts, setAllPayOuts] = useState<Payout[]>([]);
  const [source, setSource] = useState('ALL');
  const [sourceId, setSourceId] = useState(null);
  const [paymentGroupedDetails, setPaymentGroupedDetails] = useState<{
    source: string;
    sourceId: number | null;
    sourceTranslation: string;
  }>({
    source: '',
    sourceId: null,
    sourceTranslation: '',
  });
  const [status, setStatus] = useState('PENDING');
  const [businessIdentifier, setBusinessIdentifier] = useState('');
  const [businessName, setBusinessName] = useState('');
  const PAGE_SIZE = 10;
  const [view, setView] = useState<'payments' | 'grouped'>('payments');
  const [openWithoutPayrollDialog, setOpenWithoutPayrollDialog] =
    useState(false);

  const generateQuery = ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    let query = '';
    if (source) query += `source=${source}&`;
    if (sourceId) query += `sourceId=${sourceId}&`;
    if (businessIdentifier)
      query += `businessIdentifier=${businessIdentifier}&`;
    if (businessName) query += `businessName=${businessName}&`;
    if (page) query += `page=${page}&pageSize=${pageSize}`;
    if (status) query += `status=${status}&`;
    return query;
  };

  const fetchPaymentsOut = async ({
    page,
    pageSize,
  }: {
    page: number;
    pageSize: number;
  }) => {
    setLoading(true);
    try {
      const query = generateQuery({ page, pageSize });
      const response = await getAllPayouts(dispatch, query);
      setPayouts(response.payments);
      setFilteredPayouts(response.payments);
      setAllPayOuts(response.payouts);
      setLoading(false);

      return response;
    } catch (error) {
      alert('Error al cargar los pagos');
    }
    setLoading(false);
  };

  useEffect(() => {
    void fetchPaymentsOut({ page: 0, pageSize: PAGE_SIZE });
  }, []);

  const handleOpen = () => {
    setOpen(true);
  };
  const handleOpenSolicitudes = () => {
    setOpenWithoutPayrollDialog(true);
  };

  const filters: Filter[] = [
    {
      label: 'Rut destino',
      type: 'text',
      value: businessIdentifier,
      setter: setBusinessIdentifier,
    },
    {
      label: 'Nombre destino',
      type: 'autocomplete',
      value: businessName,
      key: 'businessName',
      setter: setBusinessName,
    },
    {
      label: 'Fuente',
      type: 'select',
      value: source,
      setter: setSource,
      options: [
        { label: 'Todas', value: 'ALL' },
        { label: 'Factoring', value: 'factoring-request' },
        { label: 'Confirming', value: 'confirming-request' },
        { label: 'Crédito', value: 'credit-request' },
        { label: 'Pago proveedores', value: 'payment-request' },
        { label: 'Dev. pasivos', value: 'payment-request-passive' },
      ],
    },
    {
      label: 'Id',
      type: 'text',
      value: sourceId,
      setter: setSourceId,
    },
    {
      label: 'Estado',
      type: 'select',
      value: status,
      setter: setStatus,
      options: [
        { label: 'Todas', value: 'ALL' },
        { label: 'Ejecutada', value: 'COMPLETED' },
        { label: 'Pendiente', value: 'PENDING' },
      ],
    },
  ];

  const triggerSearch = async () => {
    setLoading(true);
    await fetchPaymentsOut({ page: 0, pageSize: PAGE_SIZE });
    setLoading(false);
  };

  const buttons: CustomButtonProps[] = [
    {
      name: 'Sin nómina',
      action: handleOpenSolicitudes,
      color: 'warning',
      disabled: !hasAnyPermissions(user, ['financing_payout_view']),
    },
    {
      name: 'Buscar',
      action: () => {},
      color: 'secondary',
      customButton: (
        <GlobalSearcher
          filters={filters}
          triggerSearch={triggerSearch}
          dispatch={dispatch}
          disableSearch={loading}
        />
      ),
    },
    {
      name: 'Pago a proveedores',
      action: handleOpen,
      color: 'primary',
      disabled: !hasAnyPermissions(user, ['financing_payout_new']),
    },
  ];

  const refreshPaymentGroupDetails = async (
    source: string,
    sourceId: string,
  ) => {
    try {
      let paymentGroupedDetailResponse = await getCompanyPayoutGroup(
        dispatch,
        source,
        parseInt(sourceId),
      );
      if (source === 'factoring-request') {
        const requestRsponse = await getRecieverFactoringRequest(dispatch, {
          requestId: sourceId,
        });
        paymentGroupedDetailResponse = {
          ...paymentGroupedDetailResponse,
          ...requestRsponse,
        };
      }
      setPaymentGroupedDetails(paymentGroupedDetailResponse);
    } catch (error) {
      console.error('error', error);
      alert('Error al actualizar los detalles del pago');
    }
  };

  const handleDetails = async (id: string) => {
    const [source, sourceId] = id.split('/');
    const payment: Payout | undefined = payouts.find(
      (payout: { source: string; sourceId: string }) =>
        payout.source === source && payout.sourceId === sourceId,
    );
    if (payment) {
      try {
        setLoading(true);
        await refreshPaymentGroupDetails(
          payment.source,
          payment.sourceId.toString(),
        );
        setOpenDetails(true);
      } catch (error) {
        console.error('error', error);
        alert('Error al cargar los pagos');
      }
      setLoading(false);
    }
  };

  const actions = [
    {
      action: handleDetails,
      actionLabel: 'Detalles',
    },
  ];

  if (loading) return <CenteredCircularProgress type="layout" />;

  return (
    <div>
      <HeaderWithActions
        customTitle={
          <Typography variant="body1">
            {view === 'payments'
              ? 'Acá puedes ver los pagos agrupados por fuente. '
              : 'Acá puedes ver los pagos realizados.'}
            <span
              style={{ color: 'blue', cursor: 'pointer' }}
              onClick={() =>
                setView(view === 'payments' ? 'grouped' : 'payments')
              }
            >
              {view === 'payments'
                ? ' Ver todos las transferencias'
                : ' Ver pagos agrupados'}
            </span>
          </Typography>
        }
        buttons={buttons}
        variant="body1"
      />
      {view === 'payments' ? (
        <EnhancedTable
          exportToFile="xlsx"
          dataHeaders={headers}
          data={filteredPayouts}
          rowsPerPageDefault={50}
          actions={actions}
          showActions
          lastElement
        />
      ) : (
        <EnhancedTable
          exportToFile="xlsx"
          dataHeaders={payoutsRawHeaders}
          data={allPayOuts}
          rowsPerPageDefault={50}
          lastElement
        />
      )}
      {open && (
        <NewPayOut
          setOpen={setOpen}
          open={open}
          setSuccessOpen={setSuccess}
          setFailureOpen={setFailure}
          setUp={triggerSearch}
        />
      )}
      {openDetails && (
        <PayOutDetails
          open={openDetails}
          setOpen={setOpenDetails}
          paymentGroupedDetails={paymentGroupedDetails}
          setSuccess={setSuccess}
          setFailure={setFailure}
          setUp={triggerSearch}
          refreshPaymentGroupDetails={refreshPaymentGroupDetails}
        />
      )}

      {/* Solicitudes sin nómina dialog */}
      {openWithoutPayrollDialog && (
        <WithoutPayroll
          open={openWithoutPayrollDialog}
          setOpen={setOpenWithoutPayrollDialog}
        />
      )}

      <CustomAlert
        open={success !== ''}
        title="Éxito"
        onClose={() => {
          setSuccess('');
        }}
        text={success}
        severity="success"
        size="small"
        variant="outlined"
      />
      <CustomAlert
        open={failure !== ''}
        title="Error"
        onClose={() => {
          setFailure('');
        }}
        text={failure}
        severity="error"
        size="small"
        variant="outlined"
      />
    </div>
  );
};

export default Payouts;
